import clsx from "clsx"
import { useRef, useState } from "react"

import { faIcon } from "@utils/faIcon"
import { ifSpaceOrEnter } from "@utils/keyboard"

import { Button, Icon, IconButton } from "@atoms"

import usePopover from "@molecules/Popover/Popover.store"
import { useSelectContext } from "@molecules/Select/Select.context"

import styles from "./SelectListInput.module.scss"
import type { SelectListInputProps } from "./SelectListInput.types"

export default function SelectListInput(props: SelectListInputProps) {
    const {
        className,
        prefix,
        suffix,
        placeholder = "",
        confirmLabel,

        isDisabled = false,
        isSelected: controlledIsSelected,
        _isFocused: controlledIsFocused,
        isEditing: controlledIsEditing,
    } = props

    const { setSelectedItem, selectedItem, customValue, setCustomValue } = useSelectContext()

    const { setIsOpen: setIsOpenSelect } = usePopover()

    const [inputValue, setInputValue] = useState<string>(customValue || "")
    const [isEditing, setIsEditing] = useState<boolean>(!customValue || !!controlledIsEditing)
    const [isInputFocused, setIsInputFocused] = useState<boolean>(false)
    const inputRef = useRef<HTMLInputElement | null>(null)
    const isValueConfirmed = !!customValue
    const isEmpty = inputValue.length === 0
    const isCurrentValueSelected =
        (selectedItem?.value === inputValue && inputValue.length > 0 && !isEditing) || controlledIsSelected

    const handleApply = () => {
        setSelectedItem({ value: inputValue, label: inputValue })
        setIsEditing(false)
        setCustomValue?.(inputValue)
    }

    const enableEditing = () => {
        setIsEditing(true)
    }

    const onConfirmButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation()
        handleApply()
    }

    const onEditButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation()
        enableEditing()
    }

    const onContainerClick = () => {
        if (isInputFocused || isDisabled || inputValue.length === 0) {
            return
        } else {
            setIsOpenSelect(false)
            handleApply()
        }
    }

    return (
        <div
            className={clsx(styles.base, className, {
                [styles.disabled]: isDisabled,
                [styles.selected]: isCurrentValueSelected,
                [styles.focused]: controlledIsFocused,
            })}
            onClick={onContainerClick}
            onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
                if (!isEditing) {
                    ifSpaceOrEnter<HTMLDivElement>(event, onContainerClick, false)
                }
            }}
            tabIndex={isDisabled ? -1 : 0}
            aria-disabled={isDisabled}
            aria-selected={isCurrentValueSelected}
        >
            <div className={styles.prefixInputSuffix}>
                {prefix && (
                    <div className={styles.prefixSuffix}>
                        {isValueConfirmed && !isInputFocused && !isEditing ? "Custom:" : prefix}
                    </div>
                )}
                {isValueConfirmed && !isEditing ? (
                    <span className={styles.input}>
                        <div className={styles.confirmedLabel}>
                            {[prefix, inputValue, suffix].filter(Boolean).join(" ")}
                        </div>
                    </span>
                ) : (
                    <input
                        className={clsx(styles.input)}
                        ref={inputRef}
                        placeholder={placeholder}
                        value={inputValue}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setInputValue(e.target.value)
                        }}
                        onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                            if (event.code === "Enter") {
                                handleApply()
                            }
                        }}
                        onFocus={() => setIsInputFocused(true)}
                        onBlur={() => setIsInputFocused(false)}
                        onClick={onContainerClick}
                        autoFocus={true}
                        tabIndex={-1}
                    />
                )}
                {suffix && (!isValueConfirmed || isEditing) && <div className={styles.prefixSuffix}>{suffix}</div>}
            </div>

            {isEditing ? (
                <Button
                    type="button"
                    size="md"
                    variant="solid"
                    onClick={onConfirmButtonClick}
                    noDebounce={true}
                    isDisabled={isEmpty}
                    onKeyDown={(event) => {
                        ifSpaceOrEnter(event, handleApply)
                    }}
                >
                    {confirmLabel}
                </Button>
            ) : (
                <IconButton
                    type="button"
                    size="xl"
                    variant="ghost"
                    aria-label="Edit"
                    onClick={onEditButtonClick}
                    noDebounce={true}
                    onKeyDown={(event) => {
                        event.stopPropagation()
                        ifSpaceOrEnter(event, enableEditing)
                    }}
                    icon={faIcon({ name: "pen", style: "regular" })}
                />
            )}
            {isCurrentValueSelected && !isEditing && (
                <Icon icon={faIcon({ name: "circle-check", style: "solid" })} size={16} container="fixedHeight" />
            )}
        </div>
    )
}
